import { useSyncExternalStore } from 'react';

import {
  breakdownColors,
  darkBreakdownColors,
  darkPlotColors,
  plotColors,
  subscribePaletteChange,
} from './plot-colors';

export type ChartTheme = 'light' | 'dark';

function readChartTheme(): ChartTheme {
  if (typeof document === 'undefined') {
    return 'light';
  }
  const marker =
    document.documentElement.getAttribute('data-theme') ??
    document.body.getAttribute('data-theme');
  return marker === 'dark' ? 'dark' : 'light';
}

function readIsNewTheme(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.body.getAttribute('data-theme') !== null;
}

let currentTheme: ChartTheme = readChartTheme();
let currentIsNewTheme: boolean = readIsNewTheme();
let version = 0;
const listeners = new Set<() => void>();

function emit() {
  version += 1;
  for (const listener of listeners) {
    listener();
  }
}

if (typeof document !== 'undefined') {
  const observer = new MutationObserver(() => {
    const nextTheme = readChartTheme();
    const nextIsNew = readIsNewTheme();
    if (nextTheme !== currentTheme || nextIsNew !== currentIsNewTheme) {
      currentTheme = nextTheme;
      currentIsNewTheme = nextIsNew;
      emit();
    }
  });
  const options: MutationObserverInit = {
    attributes: true,
    attributeFilter: ['data-theme'],
  };
  observer.observe(document.documentElement, options);
  observer.observe(document.body, options);
  subscribePaletteChange(emit);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): number {
  return version;
}

function getServerSnapshot(): number {
  return 0;
}

export function useChartTheme() {
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const theme = currentTheme;
  const isNewTheme = currentIsNewTheme;
  const palette = theme === 'dark' ? darkPlotColors : plotColors;
  const getPlotColor = (index: number): string =>
    palette[index % palette.length] ?? palette[0] ?? '#7dbdf0';

  const themedBreakdown =
    theme === 'dark' ? darkBreakdownColors : breakdownColors;
  const breakdownPalette = isNewTheme ? themedBreakdown : palette;

  return { theme, isNewTheme, palette, getPlotColor, breakdownPalette };
}
